// The evidence for zero lost registrations, drawn for the person it was written
// for.
//
// xp/operator's own header says this is "the one report that has to be
// believable by somebody who was not there and has no reason to take our word
// for it", and names the client as the third audience. Until now the client had
// no way to read it. Nothing here computes any part of it: the verdict, the
// reasons, the classification of every gap and the sentence under every device
// are all that package's, and this file formats them.
//
// THE VERDICT IS THREE-WAY AND THE SCREEN KEEPS IT THAT WAY. `complete`,
// `incomplete` and `unproven` are not two good outcomes and one bad one.
// `unproven` means nothing is known to be wrong and the report cannot show that
// nothing is — a tablet on the roster that has never synced, a device whose
// trust we withdrew, a queue nobody has confirmed — and drawing it in the same
// green as `complete` would turn the whole file's pessimism into decoration.
// decide() is deliberately ordered so that its default answer is never
// `complete`; this screen's job is to not undo that at the last step.
//
// EVERY REASON IS DRAWN AND NONE IS TRUNCATED. The reasons are the report. A
// verdict with the first two of its five reasons under it is a verdict somebody
// acts on without the sentence naming the device that has never been heard from,
// and the reason list is already ordered by consequence rather than by chance.
//
// WHAT IS NOT HERE: any way to supply a device roster. The endpoint takes one —
// it is why reconciliation is a POST rather than a GET — and this console sends
// none, so every report it shows is against the devices enrolled for the
// activation. That is a list of what was authorised rather than of what was on
// the counter, the difference matters, and `roster_note` says so on the screen
// in the server's own words. A roster is two crew members and a clipboard, which
// is a capture surface rather than a reporting one; building a box for it here
// would produce a list typed by whoever is looking at the report, which is the
// one person who was not at the stand. It is written up in
// xp/CONSOLE-USABLE-REPORT.md.

import { useEffect, useState, type ReactNode } from "react";
import { NoticeBox } from "@nova/desk-ui/parts";
import type {
  DeviceReconciliation,
  Reconciliation as ReconciliationReport,
  SequenceGap,
  UnexpectedDevice,
  WebsiteArrivals,
} from "../../../operator/src/reconciliation.js";
import { readReport, type ApiOptions, type Failure } from "../api/client.js";
import { count, moment, UNKNOWN } from "../operator/present.js";
import { ReadFailed } from "./ReadFailed.js";
import { useShow } from "./Show.js";
import type { SessionToken } from "../session/store.js";

export type ReconciliationProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly onSessionEnded: (detail: string) => void;
  readonly role: string;
  readonly onEnrol: () => void;
};

type State =
  | { readonly phase: "reading" }
  | { readonly phase: "read"; readonly report: ReconciliationReport }
  | { readonly phase: "failed"; readonly failure: Failure };

export function Reconciliation({
  api,
  token,
  activationId,
  onSessionEnded,
  role,
  onEnrol,
}: ReconciliationProps): ReactNode {
  const show = useShow();
  const [state, setState] = useState<State>({ phase: "reading" });
  // Bumped by "Ask again"; the effect below reads again whenever it changes.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (show.status !== "ready") return;
    let cancelled = false;
    setState({ phase: "reading" });

    void (async () => {
      const result = await readReport<ReconciliationReport>(api, token, "reconciliation", activationId);
      if (cancelled) return;

      if (result.ok) {
        setState({ phase: "read", report: result.value });
        return;
      }

      setState({ phase: "failed", failure: result });
      if (result.kind === "session_ended") onSessionEnded(result.detail);
    })();

    return () => {
      cancelled = true;
    };
  }, [api, token, activationId, onSessionEnded, show.status, attempt]);

  // The design keeps the page's head on screen and puts the failure where the
  // report would have been, with "Ask again" (TASKS §143, step 5).
  // "Ask again" asks again for whatever failed: the show's own read, which the
  // report waits on, or the report itself.
  const retry = () => (show.status === "failed" ? show.reload() : setAttempt((n) => n + 1));
  const failure = show.status === "failed" ? show.failure : state.phase === "failed" ? state.failure : null;
  if (failure !== null) {
    return (
      <div className="stack">
        <header className="head">
          <h1>Did everything arrive?</h1>
          <p className="lede">Activation {activationId}</p>
        </header>
        <ReadFailed
          what="Could not check arrivals"
          failure={failure}
          role={role}
          onEnrol={onEnrol}
          notCounted="No arrival result is shown because the report did not load."
          onRetry={retry}
        />
      </div>
    );
  }

  if (show.status !== "ready" || state.phase !== "read") {
    return (
      <div className="stack">
        <header className="head">
          <h1>Checking arrivals…</h1>
          <p className="lede">Comparing the tablet records with what reached the service.</p>
        </header>
      </div>
    );
  }

  const report = state.report;
  const totals = report.totals;

  return (
    <div className="stack">
      <header className="head">
        <h1>Did everything arrive?</h1>
        <p className="lede">
          Activation {report.activation_id ?? activationId} · read at {moment(report.generated_at)} ·{" "}
          {report.access_note ?? "Access details are unavailable."}
        </p>
      </header>

      {/* The verdict, in the server's own sentence, in the colour that verdict
          deserves rather than in the colour of a finished job. */}
      <NoticeBox kind={verdictKind(report.verdict)} title={VERDICT_HEADING[report.verdict] ?? report.verdict}>
        <p>{report.verdict_sentence ?? ""}</p>
      </NoticeBox>

      <section>
        <h2 className="sec">What we found</h2>
        <ul className="reasons">
          {(report.reasons ?? []).map((reason, index) => (
            // Indexed key for the reason Operator.tsx's attention list gives:
            // these have no identity of their own, the list is replaced whole on
            // every read, and nothing is reordered in place.
            <li key={index}>{reason}</li>
          ))}
        </ul>
        {report.roster_note ? <p className="quiet">{report.roster_note}</p> : null}
      </section>

      <section>
        <h2 className="sec">Totals</h2>
        <div className="figs">
          <Figure label="Tablets heard from" value={count(totals?.devices_heard_from)} />
          <Figure label="Events arrived" value={count(totals?.events_arrived)} />
          <Figure label="Events counted" value={count(totals?.events_counted)} />
          <Figure label="Corrections" value={count(totals?.events_voided)} />
          <Figure label="Registrations" value={count(totals?.registrations_counted)} />
          <Figure label="Gaps in a sequence" value={count(totals?.sequence_gaps)} />
          <Figure label="Events lost" value={count(totals?.events_lost)} />
          <Figure label="Events unaccounted for" value={count(totals?.events_unresolved)} />
          {/* Null when any one tablet's queue is unknown, because xp/operator
              refuses to publish a partial sum that reads as a total. Drawn
              through the same presenter as every other nullable figure, so it
              says so rather than showing the sum of what happened to be known. */}
          <Figure label="Still on a tablet" value={count(totals?.events_outstanding)} />
        </div>
      </section>

      <Website website={report.website ?? null} />

      <section>
        <h2 className="sec">Tablets</h2>
        <Devices devices={report.devices ?? []} />
      </section>

      <Gaps devices={report.devices ?? []} />

      <Unexpected devices={report.unexpected_devices ?? []} />
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="fig">
      <span className="fig-l">{label}</span>
      <span className={value === UNKNOWN ? "fig-v num unk" : "fig-v num"}>{value}</span>
    </div>
  );
}

function Website({ website }: { website: WebsiteArrivals | null }): ReactNode {
  if (website === null) return null;
  return (
    <section>
      <h2 className="sec">Website</h2>
      <div className="panel">
        <p style={{ margin: 0 }}>
          {count(website.events_arrived)} event{website.events_arrived === 1 ? "" : "s"} arrived from
          the public registration site, not from a tablet. {count(website.events_counted)} counted
          {website.events_voided === 0
            ? "."
            : `, ${count(website.events_voided)} corrected.`}
        </p>
      </div>
    </section>
  );
}

/**
 * One row per tablet this report accounts for.
 *
 * `outstanding` is the column the whole report turns on and it is nullable, so
 * it goes through the presenter like every other one: a tablet that has not said
 * what it is holding is not a tablet holding nothing, and it is the tablet that
 * must not be packed. `outstanding_source` is drawn beside it because "the
 * device said so" and "a crew member confirmed it" are different kinds of
 * evidence for the same nought.
 */
function Devices({ devices }: { devices: DeviceReconciliation[] }): ReactNode {
  if (devices.length === 0) {
    return (
      <div className="panel">
        <p style={{ margin: 0 }}>No tablet is enrolled and none has contacted the service.</p>
      </div>
    );
  }

  return (
    <div className="tablewrap">
      <div className="tablescroll">
        <table>
          <thead>
            <tr>
              <th>Tablet</th>
              <th>Heard from</th>
              <th className="num">Arrived</th>
              <th className="num">Counted</th>
              <th className="num">Corrections</th>
              <th>Sequence</th>
              <th className="num">Lost</th>
              <th className="num">Still holding</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device) => (
              // A tablet never heard from is the design's quiet row, marked at
              // its edge: there is nothing on it yet to read, and it is the one
              // the reasons above are most likely to be about.
              <tr key={device.device_id} className={device.heard_from ? undefined : "silent"}>
                <td data-label="Tablet">
                  <span className="mono">{device.device_id}</span>
                  <span className="cell-sub">{device.says}</span>
                </td>
                <td data-label="Heard from">
                  {/* The design puts where the tablet's count comes from in this
                      column. Never heard from, and revoked, still say so under it. */}
                  {device.outstanding_source === "unknown" ? (
                    <span className="unk">{HELD_SOURCE.unknown}</span>
                  ) : (
                    HELD_SOURCE[device.outstanding_source] ?? device.outstanding_source
                  )}
                  {device.heard_from ? null : <span className="cell-sub">never heard from</span>}
                  {device.revoked_at === null ? null : (
                    <span className="cell-sub">revoked {moment(device.revoked_at)}</span>
                  )}
                </td>
                <td data-label="Arrived" className="num">{count(device.events_arrived)}</td>
                <td data-label="Counted" className="num">{count(device.events_counted)}</td>
                <td data-label="Corrections" className="num">{count(device.events_voided)}</td>
                <td data-label="Sequence">
                  {device.first_sequence === null || device.last_sequence === null ? (
                    <span className="unk">{UNKNOWN}</span>
                  ) : (
                    `${device.first_sequence}–${device.last_sequence}`
                  )}
                </td>
                <td data-label="Lost" className="num">{count(device.events_lost)}</td>
                <td data-label="Still holding" className="num">
                  <Unknownable text={count(device.outstanding)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Every gap in a device's sequence, with the server's classification of it.
 *
 * A gap is not a finding on its own: a device used at two shows produces one
 * sequence spanning both, so numbers this activation never used are not
 * automatically missing. classifyGap decides which of three things a gap is, and
 * the same absence is proof for the service and an open question for a client
 * viewer — so the explanation travels with each one rather than the count being
 * summarised into a single number nobody can act on.
 */
function Gaps({ devices }: { devices: DeviceReconciliation[] }): ReactNode {
  const gaps: (SequenceGap & { device: string })[] = [];
  for (const device of devices) {
    for (const gap of device.gaps ?? []) gaps.push({ ...gap, device: device.device_id });
  }

  if (gaps.length === 0) return null;

  return (
    <section>
      <h2 className="sec">Gaps in tablet sequences</h2>
      <ul className="reasons">
        {gaps.map((gap, index) => (
          <li key={index} className={gap.verdict === "lost" ? "gap-lost" : undefined}>
            <strong>
              {gap.device} · {gap.from_sequence === gap.to_sequence
                ? gap.from_sequence
                : `${gap.from_sequence}–${gap.to_sequence}`}{" "}
              · {GAP_HEADING[gap.verdict] ?? gap.verdict}
            </strong>
            <p>{gap.explanation}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Tablets that sent events here and are not on the roster.
 *
 * Not a footnote, on the report's own reasoning: a roster missing a device we
 * have heard from may equally be missing one we have not, and the second kind is
 * the one that costs registrations.
 */
function Unexpected({ devices }: { devices: UnexpectedDevice[] }): ReactNode {
  if (devices.length === 0) return null;

  return (
    <section>
      <h2 className="sec">Unexpected tablets</h2>
      <ul className="reasons">
        {devices.map((device) => (
          <li key={device.device_id}>
            <strong className="mono">{device.device_id}</strong>
            <p>
              {count(device.events_arrived)} event(s) arrived from it. {device.explanation}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Unknownable({ text }: { text: string }): ReactNode {
  return text === UNKNOWN ? <span className="unk">{text}</span> : <>{text}</>;
}

/**
 * The heading over each verdict.
 *
 * `unproven` says what it is rather than borrowing a word from either of the
 * other two. "No problems found" would be the friendly phrasing and it is
 * exactly the misreading the whole report is written against.
 */
const VERDICT_HEADING: Record<string, string> = {
  complete: "Everything captured arrived",
  incomplete: "Something captured has not arrived",
  unproven: "Not enough information yet",
};

/**
 * The class for a verdict, refusing to invent one.
 *
 * Only `complete` is drawn as good. Everything else is amber, as the design
 * draws it: `incomplete` used to be red here, and the design's amber says the
 * same thing a red would, that something is to be acted on (TASKS §143, step 5).
 * `unproven` is a warning and never the success style, and so is a verdict this
 * build has never heard of: a word the server used and this bundle does not know
 * is not a word to draw in green.
 */
function verdictKind(verdict: string): "good" | "amber" {
  return verdict === "complete" ? "good" : "amber";
}

const GAP_HEADING: Record<string, string> = {
  lost: "captured and never arrived",
  captured_elsewhere: "used at another activation",
  unresolved: "cannot be accounted for from here",
};

const HELD_SOURCE: Record<string, string> = {
  device_report: "the tablet's own report",
  crew_confirmation: "confirmed by crew",
  unknown: "not reported",
};
